import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import BookingForm from "@/components/BookingForm";
import { ArrowRight, Headphones, Film, CheckCircle2 } from "lucide-react";

const STEPS = [
  ["01", "Send your brief", "Tell us the language, age, voice style and deadline for your project."],
  ["02", "We cast", "Our specialist team selects suitable child actors from our international network."],
  ["03", "Listen & approve", "Review proposed voices or commission custom auditions."],
  ["04", "Record", "The approved voice is professionally directed and recorded."],
  ["05", "Delivery", "Production-ready audio is delivered by download link or FTP."],
];

export default function Casting() {
  const [params] = useSearchParams();
  const prefillVoice = params.get("voice") || "";
  // Mandatory intent gate — form stays hidden until the visitor confirms they want to hire.
  // Coming from a voice profile ("Request this voice") is already a hire intent, so skip the gate.
  const [intent, setIntent] = useState(prefillVoice ? "hire" : null);

  return (
    <>
      <SEO title="Book a Voice — Cast Child Voice Talent" path="/casting"
        description="Send your brief and we'll cast the right child voices for your production. Directed recordings in 30+ languages." />
      <section className="bg-brand-ink text-white">
        <div className="container-x py-14 md:py-16">
          <span className="label-eyebrow text-brand-purple">Casting service</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">Let us cast for you</h1>
          <p className="mt-3 max-w-2xl text-zinc-300">Not sure which child fits best? Send us your briefing and we'll propose the right voices.</p>
        </div>
      </section>

      <section className="container-x section-y">
        <div className="grid gap-5 md:grid-cols-5">
          {STEPS.map(([n, t, d]) => (
            <div key={n} className="rounded-2xl border border-zinc-200 bg-white p-5">
              <span className="font-mono text-sm font-bold text-brand-violet">{n}</span>
              <h3 className="mt-2 font-heading text-lg font-bold">{t}</h3>
              <p className="mt-1 text-sm text-zinc-500">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white section-y">
        <div className="container-x max-w-3xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight">Looking to cast a voice? You're in the right place.</h2>
          <p className="mt-2 text-zinc-600">This service is for producers, agencies, studios, brands, developers and other clients looking to hire child voice talent for a production.</p>
          <p className="mt-3 text-sm text-zinc-500">
            Want to become voice talent instead? Please do not use this form. <Link to="/join-our-talent" className="font-semibold text-brand-violet hover:underline">View our talent application requirements</Link>.
          </p>

          {intent !== "hire" ? (
            <div className="mt-8" data-testid="intent-gate">
              <h3 className="font-heading text-xl font-bold">What would you like to do?</h3>
              <div className="mt-5 grid gap-5 md:grid-cols-2">
                {/* Primary path — hire */}
                <div className="flex flex-col rounded-3xl border-2 border-brand-violet bg-red-50/40 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-violet text-white"><Film size={22} /></span>
                  <h4 className="mt-4 font-heading text-lg font-bold">I need to hire a child voice actor</h4>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-600">I have a production, campaign, game, animation, film, series or other project and would like VoiceOverKids.Agency to help me find the right voice.</p>
                  <button type="button" data-testid="intent-hire" onClick={() => setIntent("hire")}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-brand-violet px-6 py-3 font-bold text-white transition hover:bg-brand-violetHover">
                    Start Casting Request <ArrowRight size={16} />
                  </button>
                </div>
                {/* Secondary path — apply */}
                <div className="flex flex-col rounded-3xl border border-zinc-200 bg-white p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-ink text-white"><Headphones size={22} /></span>
                  <h4 className="mt-4 font-heading text-lg font-bold">I / my child want to become a voice actor</h4>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-600">Looking for representation or hoping to join our talent roster? Casting requests are not used for talent applications.</p>
                  <Link to="/join-our-talent" data-testid="intent-apply"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-zinc-300 px-6 py-3 font-bold text-zinc-800 transition hover:border-brand-violet hover:text-brand-violet">
                    View Talent Application Requirements <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-8" data-testid="casting-form-wrap">
              <div className="mb-6 flex items-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm font-semibold text-green-800">
                <CheckCircle2 size={16} /> Casting / booking request for a production
              </div>
              <h3 className="font-heading text-2xl font-bold tracking-tight">Start a booking request</h3>
              <p className="mt-2 text-zinc-600">Tell us about your project. This form is for clients looking to cast or book VoiceOverKids talent. This is not a talent application or audition form.</p>
              <p className="mt-2 mb-8 text-zinc-600">Once submitted, we'll check talent availability and confirm pricing with you before any booking is made.</p>
              <BookingForm initialChildName={prefillVoice} />
            </div>
          )}
        </div>
      </section>
    </>
  );
}
